import React, { useState, useEffect, useRef, useCallback } from "react";
import bannerImg from "../assets/images/House5.jpg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faArrowRightLong,
  faAward,
  faBath,
  faBed,
  faEnvelope,
  faLocationDot,
  faMagnifyingGlass,
  faPhone,
  faStar,
} from "@fortawesome/free-solid-svg-icons";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay, EffectFade, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/effect-fade";
import avatar1 from "../assets/images/avatar1.png";
import avatar2 from "../assets/images/avatar3.png";
import placeholder from "../assets/images/house-placeholder.webp";
import { Link } from "react-router-dom";
import imgs from "./temp.json";
import ReactImageGallery from "react-image-gallery";
import calc from "../assets/images/shutterstock_2489188913-min.jpg";
import report from "../assets/images/shutterstock_699442570-min.jpg";
import house from "../assets/images/shutterstock_617467400-min.jpg";
import constants from "../constants/constants";
import DEMO_LISTINGS from "../constants/demoListings";
import video from "../assets/images/1026290987-preview.mp4";
import video2 from "../assets/images/1096898873-preview.mp4";
import formattingFunctions from "../functions/formatting";
import houseInterior from "../assets/images/shutterstock_2304013113-min.jpg";
// import houseInterior from "../assets/images/stock-photo-luxury-hotel-bathroom-interior-with-bathtub-and-side-table-concrete-floor-bathing-space-in-modern-2467634645.jpg"

import houseInterior2 from "../assets/images/shutterstock_1935730303-min.jpg";
import skyline from "../assets/images/shutterstock_2467634645-min.jpg";
import condos from "../assets/images/shutterstock_379744201-min.jpg";
import condosLarge from "../assets/images/shutterstock_379744201-min-large.jpg";
import SEO, { pageSEO } from "../components/seo.component";
import isReactSnap from "../utils/isReactSnap";
import globalState from "../utils/globalState";
import { useUserParams } from "../hooks/useUserParams";

// Get all images from the images folder using require.context
const imageContext = require.context('../assets/images', false, /\.(jpg|jpeg|png|webp)$/);

// Custom hooks
const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024 // Default fallback size
  );

  useEffect(() => {
    if (typeof window === "undefined") return;

    const onWindowResize = () => {
      setWindowSize(window.innerWidth);
    };

    window.addEventListener("resize", onWindowResize);
    return () => window.removeEventListener("resize", onWindowResize);
  }, []);

  return windowSize;
};

const useScrollVisibility = () => {
  const isScrolledIntoView = useCallback((elem) => {
    if (typeof window === "undefined") return false;

    if (elem) {
      const rect = elem.getBoundingClientRect();
      const elemTop = rect.top;
      const elemBottom = rect.bottom;
      return elemTop >= 0 && elemBottom <= window.innerHeight;
    }
    return false;
  }, []);

  const isScrolledIntoViewTop = useCallback((elem) => {
    if (typeof window === "undefined") return false;

    if (elem) {
      const rect = elem.getBoundingClientRect();
      const elemTop = rect.top;
      return elemTop - window.innerHeight < 0;
    }
    return false;
  }, []);

  return { isScrolledIntoView, isScrolledIntoViewTop };
};

export default function Homepage() {
  const proxyUrl = "https://cors-anywhere.herokuapp.com/";
  const windowSize = useWindowSize();
  const { isScrolledIntoView, isScrolledIntoViewTop } = useScrollVisibility();

  const {
    getCurrentAgentName,
    getCurrentAgentTitle,
    getCurrentAgentNumber,
    getCurrentAgentEmail,
    getCurrentBrokerage,
    getCurrentQuote,
    getCurrentHeadshot,
    getCurrentAbout
  } = useUserParams();

  // Refs
  const contact = useRef();
  const loaded = useRef(false);
  const loadedRef = useRef(false);
  const locationRef = useRef(null);
  const agentsRef = useRef(null);
  const slideUpElements = useRef([]);
  const slideInElements = useRef([]);
  const slideRightElements = useRef([]);

  // State
  const [listings, setListings] = useState(DEMO_LISTINGS);
  const [media, setMedia] = useState(imgs.thumbs.value);
  const [formatted, setFormatted] = useState({});
  const [offset, setOffset] = useState(0);
  const [keyword, setKeyword] = useState("");

  // Function to get first image by ListingKey from local images folder
  const getFirstImageByListingKey = (listingKey) => {
    if (!listingKey) {
      return placeholder;
    }

    // Get all image file paths from require.context
    const imageKeys = imageContext.keys();
    
    // Filter images that start with the ListingKey prefix
    const matchingImages = imageKeys
      .filter(key => {
        // Extract filename from the key (remove './' prefix)
        const filename = key.replace('./', '');
        // Check if filename starts with ListingKey followed by '_' or ' '
        return filename.startsWith(listingKey + '_') || filename.startsWith(listingKey + ' ');
      })
      .map(key => {
        // Get the actual image module
        const imagePath = imageContext(key);
        // Extract filename for sorting
        const filename = key.replace('./', '');
        return { path: imagePath, filename };
      })
      .sort((a, b) => {
        // Sort: main photo first, then others alphabetically
        if (a.filename.includes('main photo')) return -1;
        if (b.filename.includes('main photo')) return 1;
        return a.filename.localeCompare(b.filename);
      });

    // Return the first matching image, or placeholder if none found
    return matchingImages.length > 0 ? matchingImages[0].path : placeholder;
  };

  // Add elements to refs
  const addToSlideUpRefs = (el) => {
    if (el && !slideUpElements.current.includes(el)) {
      slideUpElements.current.push(el);
    }
  };

  const addToSlideInRefs = (el) => {
    if (el && !slideInElements.current.includes(el)) {
      slideInElements.current.push(el);
    }
  };

  const addToSlideRightRefs = (el) => {
    if (el && !slideRightElements.current.includes(el)) {
      slideRightElements.current.push(el);
    }
  };

  // Scroll handler
  useEffect(() => {
    const handleScroll = () => {
      if (agentsRef.current && isScrolledIntoViewTop(agentsRef.current)) {
        const rect = agentsRef.current.getBoundingClientRect();
        const elemBottom = rect.bottom;
        setOffset(0 - Math.floor(elemBottom * 0.5));
      }

      slideInElements.current.forEach((el) => {
        if (isScrolledIntoView(el)) {
          el?.classList.remove("slide-left");
        }
      });

      if (locationRef.current && isScrolledIntoViewTop(locationRef.current)) {
        let interval = 0;
        slideUpElements.current.forEach((el) => {
          setTimeout(() => {
            el?.classList.remove("mt-10");
          }, interval);
          interval += 250;
        });
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isScrolledIntoView, isScrolledIntoViewTop]);

  // Load effect
  useEffect(() => {
    const handleLoad = () => {
      let interval = 0;
      slideRightElements.current.reverse().forEach((el) => {
        setTimeout(() => {
          el?.classList.remove("-ml-10");
        }, interval);
        interval += 250;
      });
    };

    window.addEventListener("load", handleLoad);
    return () => window.removeEventListener("load", handleLoad);
  }, []);

  function getData() {
    // Skip API calls during static generation
    if (isReactSnap()) {
      return Promise.resolve({ value: [] });
    }

    var body = {
      url: `https://query.ampre.ca/odata/Property?$filter=(ListAgentKey eq '${constants.AGENT_ID}' and StandardStatus eq 'Active')&$orderby=OriginalEntryTimestamp desc,ListingKey&$top=12`,
      needAuth: true,
      bearerToken: constants.TOKEN,
    };

    return new Promise(async (resolve, reject) => {
      try {
        let request = `odata/Property(Q)$filter=(ListAgentKey(S)eq(S)\'${constants.AGENT_ID}\'(S)and(S)StandardStatus(S)eq(S)\'Active\')(A)$orderby=OriginalEntryTimestamp(S)desc,ListingKey(A)$top=12`;


        let data =
          constants.ENVIRONMENT === "production"
            ? await fetch(`https://${constants.DOMAIN}/api/${request}`)
            : await fetch(`http://${constants.URL}/metaGet`, {
              method: "POST",
              body: JSON.stringify(body),
              headers: {
                "Content-Type": "application/json",
              },
            });

        if (data.ok) {
          let res = await data.json();
          resolve(res);
        } else {
          reject("Failed to fetch data");
        }
      } catch (err) {
        reject(err);
      }
    });
  }

  function getMedia(listings) {
    // Skip API calls during static generation
    if (isReactSnap() || !listings || listings.length === 0) {
      return Promise.resolve({ value: [] });
    }

    // this is for development
    //var temp = `https://query.ampre.ca/odata/Media?$filter=(`

    // this is for production
    // var temp = `odata/Media?$filter=(`

    if (constants.ENVIRONMENT === "development") {
      var temp = `https://query.ampre.ca/odata/Media?$filter=ResourceRecordKey in (`;
      for (let l of listings) {
        temp = temp + "'" + l.ListingKey + "', ";
      }
      temp = temp.slice(0, -2);
      temp =
        temp +
        ") and ImageSizeDescription eq 'Medium' and PreferredPhotoYN eq true";
    } else {
      var temp = `odata/Media(Q)$filter=ResourceRecordKey(S)in(S)(`;
      for (let l of listings) {
        temp = temp + "'" + l.ListingKey + "',";
      }
      temp = temp.slice(0, -1);
      temp =
        temp +
        ")(S)and(S)ImageSizeDescription(S)eq(S)'Medium'(S)and(S)PreferredPhotoYN(S)eq(S)true";
    }
    var body = {
      url: temp,
      needAuth: true,
      bearerToken: constants.TOKEN,
      // Accept: 'application/json',
      // Authorization: 'Bearer eyJhbGciOiJIUzI1NiJ9.eyJzdWIiOiJ2ZW5kb3IvdHJyZWIvNjUyMCIsImF1ZCI6IkFtcFVzZXJzUHJkIiwicm9sZXMiOlsiQW1wVmVuZG9yIl0sImlzcyI6InByb2QuYW1wcmUuY2EiLCJleHAiOjI1MzQwMjMwMDc5OSwiaWF0IjoxNzMwMjI1OTA1LCJzdWJqZWN0VHlwZSI6InZlbmRvciIsInN1YmplY3RLZXkiOiI2NTIwIiwianRpIjoiMGYyMjk3OTY1Y2NjN2U4MSIsImN1c3RvbWVyTmFtZSI6InRycmViIn0.BMvVrtwah8swIzW38UMs6yKF-HM4QO0bZu81blYx6J4'
    };

    return new Promise(async (resolve, reject) => {
      try {
        let data =
          constants.ENVIRONMENT === "production"
            ? await fetch(`https://${constants.DOMAIN}/api/${temp}`)
            : await fetch(`http://${constants.URL}/metaGet`, {
              method: "POST",
              body: JSON.stringify(body),
              headers: {
                "Content-Type": "application/json",
              },
            });

        if (data.ok) {
          let res = await data.json();
          resolve(res);
        } else {
          reject("Failed to fetch data");
        }
      } catch (err) {
        reject(err);
      }
    });
  }

  function formatMedia(media) {
    var temp = {};

    for (let img of media) {
      if (temp[img.ResourceRecordKey]) {
        temp[img.ResourceRecordKey] = [...temp[img.ResourceRecordKey], img];
      } else {
        temp[img.ResourceRecordKey] = [img];
      }
    }
    return temp;
  }

  // Function to fetch template settings from MongoDB
  const fetchTemplateSettings = async (userId) => {
    try {
      const apiKey = process.env.REACT_APP_API_KEY;
      const response = await fetch(`https://agent.proagentsolutions.com/MongoDB/GetTemplateSettingsByUserId?userId=${userId}&apiKey=${apiKey}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const templateSettings = await response.json();
      console.log('Template settings fetched:', templateSettings);
      
      // Update global state with template settings
      if (templateSettings) {
        // Extract data from templateSettings.data if it exists
        const settingsData = templateSettings.data || templateSettings;
        
        // Map template settings to userData format
        const mappedUserData = {
          userId: userId,
          agentName: settingsData.agentName || settingsData.AGENT_NAME,
          agentTitle: settingsData.agentTitle || settingsData.AGENT_TITLE,
          agentNumber: settingsData.agentNumber || settingsData.AGENT_NUMBER,
          agentEmail: settingsData.agentEmail || settingsData.AGENT_EMAIL,
          brokerage: settingsData.brokerage || settingsData.BROKERAGE,
          // Include any additional fields from templateSettings.data
          ...settingsData
        };
        
        // Set the mapped user data
        globalState.setUserData(mappedUserData);
        
        console.log('Template settings data:', settingsData);
        console.log('Template settings mapped to user data:', mappedUserData);
      }
      
      return templateSettings;
    } catch (error) {
      console.error('Error fetching template settings:', error);
      return null;
    }
  };

  // Get userId from URL parameters
  const getURLParameters = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      userId: urlParams.get('userId')
    };
  };

  // Data fetching effect with React Snap compatibility
  useEffect(() => {
    if (!loaded.current) {
      // Get and store URL parameters
      const urlParams = getURLParameters();
      if (urlParams.userId) {
        // Set global userId
        globalState.setUserId(urlParams.userId);
        
        // Fetch additional template settings from MongoDB
        // This will also update userData and userParams cache
        fetchTemplateSettings(urlParams.userId).then(() => {
          // Debug information after template settings are loaded
          console.log('Global State Updated with Template Settings:', globalState.getDebugInfo());
        });
        
        // Debug information
        console.log('Global State Updated:', globalState.getDebugInfo());
      }
    }
  }, []);

  useEffect(() => {
    if (isReactSnap()) return; // Skip fetching during react-snap
    const isReactSnapEnv =
      typeof navigator !== "undefined" &&
      navigator.userAgent.includes("ReactSnap");
    const isBrowser = typeof window !== "undefined";

    if (isBrowser && !isReactSnapEnv && !loaded.current) {
      getData()
        .then((data) => {
          setListings(data.value);
          getMedia(data.value)
            .then((d) => {
              setMedia(d.value);
              setFormatted(formatMedia(d.value));
            })
            .catch((err) => {
              console.log(err);
            });
        })
        .catch((err) => {
          console.log(err);
        });
      loaded.current = true;
    }
  }, []);

  function slugify(str) {
    str = str.replace(/^\s+|\s+$/g, "");
    str = str.toLowerCase();
    str = str
      .replace(/[^a-z0-9 -]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-");
    return str;
  }

  const seoConfig = pageSEO.home();

  return (
    <>
      <SEO {...seoConfig} />
      <div>
        <div className="max-w-[1700px] mx-auto h-[100vh] relative flex justify-between px-12 pb-6 items-end">
          <div className=" text-black  z-10">
            <h1
              style={{ fontWeight: "200" }}
              className="libre text-unset text-7xl sm:text-8xl lg:text-9xl"
            >
              Find Your
            </h1>
            <h1
              style={{ fontWeight: "200" }}
              className="libre text-unset text-7xl sm:text-8xl lg:text-9xl"
            >
              Dream Home
            </h1>
            {/* <h1 style={{fontWeight: '200'}} className="libre text-unset text-9xl">Dream </h1>
                <h1 style={{fontWeight: '200'}} className="libre text-unset text-9xl">Home</h1> */}
            <div className="flex flex-wrap gap-6 pt-10 pl-4">
              <Link>
                <button
                  onClick={() => contact.current?.scrollIntoView()}
                  className="btn-primary"
                >
                  Contact Me
                </button>
              </Link>
              <Link to={"/listings"}>
                <button className="btn-tertiary">See Listings</button>
              </Link>
            </div>
            <div className="flex flex-col sm:flex-row mt-12 pl-4 gap-y-2 pb-6">
              <img
                src={houseInterior}
                ref={addToSlideRightRefs}
                className="para-right h-36 slide-right hidden min-[750px]:block duration-1000 ease-in-out relative -ml-10"
              />
              <img
                src={condos}
                ref={addToSlideRightRefs}
                className="para-right h-36 slide-right self-start duration-1000 ease-in-out relative -ml-10"
              />
              <img
                src={houseInterior2}
                ref={addToSlideRightRefs}
                className="para-right h-36 slide-right hidden sm:block duration-1000 ease-in-out relative -ml-10"
              />
            </div>
          </div>
          <img
            className="para-right hidden min-[1500px]:block min-[1700px]:h-[85%] pb-6"
            src={constants.HOME_IMAGE}
          />
        </div>
        {/* <div className="w-full  h-[100vh] relative flex "  >

                <div className=" w-[100%] h-full min-[1100px]:w-[50%] flex flex-col absolute min-[1100px]:relative">
                    <h1 className="text-5xl libre text-center pb-12 font-semibold min-[650px]:mt-[35%] mt-[25vh] px-10 text-white min-[1100px]:text-black">Find Your Dream Home</h1>
                    <div className="flex gap-6 self-center">
                        <div className="relative flex-1 ">
                            <input className="form-input" placeholder="Search Keyword" value={keyword} onChange={(e) => setKeyword(e.target.value)} style={{ borderRadius: '99999px' }}></input>
                            <FontAwesomeIcon icon={faMagnifyingGlass} className="absolute right-4 top-2 text-med-grey" />
                        </div>
                        <Link to={'/listings'} state={keyword}>
                            <button className="btn-rounded">Search</button>
                        </Link>
                    </div>
                    <div className="flex flex-col min-[500px]:flex-row gap-4 items-start min-[1000px]:w-[90%] w-[85vw] justify-between mx-10 absolute bottom-10">
                        <div onClick={() => contact.current.scrollIntoView()} className="cursor-pointer hover:bg-logo-main rounded-full duration-500 bg-black px-3 py-2 min-[500px]:self-center">

                            <span className="text-white"><FontAwesomeIcon icon={faPhone} className="mr-2 whitespace-nowrap" />Contact Us Now</span>

                        </div>

                        <Link to={'/listings'}>
                            <div className="flex flex-wrap gap-x-1 text-white min-[1100px]:text-black items-center ">
                                <span className="font-semibold ">See All Listings</span>
                                <FontAwesomeIcon className="pr-1" transform={{ rotate: -45 }} icon={faArrowRightLong} />
                                <div className="h-12 w-12 overflow-hidden rounded-full">
                                    <img className="h-full w-full object-cover" src={house}>
                                    </img>
                                </div>

                            </div>
                        </Link>

                    </div>
                </div>
                <div className="w-[100%] min-[1100px]:w-[50%] rounded-2xl m-5 bg-cover" style={{ backgroundImage: `url(${house})`, backgroundPosition: 'center' }}>

                </div>
            </div> */}

        <div className=" bg-logo-second w-full">
          <div className="flex text-white justify-between items-center pt-14 pb-10 px-10 gap-x-5 max-w-[1700px] mx-auto">
            <h1
              ref={addToSlideInRefs}
              className={`text-4xl libre uppercase ease-in-out duration-2000 slide-in ${
                windowSize > 700 ? "slide-left" : ""
              }`}
            >
              Featured Listings
            </h1>
            <Link
              className="underline font-semibold underline-offset-4 whitespace-nowrap "
              to={"/listings"}
            >
              View All
            </Link>
          </div>

          <div
            style={{ flexFlow: "column wrap" }}
            className="flex gap-5 max-w-[1700px]  min-[1000px]:h-[1350px] min-[1600px]:h-[900px] px-10 mx-auto"
          >
            {listings?.slice(0, 6)?.map((prop) => (
              <Link
                className={`${
                  windowSize >= 1600
                    ? "l-mosaic"
                    : windowSize >= 1000
                    ? "m-mosaic"
                    : "h-[400px]"
                }  `}
                to={`/listing/${prop.ListingKey}`}
              >
                <div className="relative hover:bg-black overflow-hidden group w-full h-full">
                  <img
                    className="w-full h-full absolute object-cover  group-hover:opacity-50  duration-1000 group-hover:scale-105"
                    src={getFirstImageByListingKey(prop.ListingKey)}
                  />
                  <div className="absolute flex flex-col w-full h-full z-50 overlay py-4 justify-end gap-y-2">
                    <p
                      style={{ fontWeight: "200" }}
                      className="libre min-[900px]:text-[1.25em] text-[1.25em] text-white px-4"
                    >
                      <FontAwesomeIcon icon={faLocationDot} className="pr-3" />
                      {prop?.StreetNumber} {prop?.StreetName}{" "}
                      {prop?.StreetSuffix}
                    </p>

                    <p className="font-bold text-white text-2xl px-4">
                      {prop.CityRegion ?? prop.CrossStreet}
                    </p>
                    <hr className="border-logo-main border-2 duration-1000 w-[20%] group-hover:w-[50%] mx-4 my-2"></hr>
                    <div className=" whitespace-nowrap flex-wrap child:px-4  flex ">
                      <div className="border-r-[1px] border-white text-white text-2xl">
                        <span>${prop.ListPrice?.toLocaleString()}</span>
                      </div>
                      <div className="border-r-[1px] border-white text-white text-2xl">
                        <span>{prop.BedroomsTotal} Beds</span>
                      </div>
                      <div className="border-r-[1px] border-white text-white text-2xl">
                        <span>{prop.BathroomsTotalInteger} Baths</span>
                      </div>
                      <div className=" text-white text-2xl">
                        {prop?.LivingAreaRange ? prop.LivingAreaRange : "--"}{" "}
                        Sqft
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* <h1 className="text-4xl text-white py-10 font-mont px-10 uppercase pt-14 max-w-[1700px] mx-auto">Featuwhite Neighbourhoods</h1>

                <div className="grid min-[750px]:grid-cols-2 px-10 max-w-[1700px] mx-auto">
                    {constants.LOCATIONS.slice(0, 4).map((location) =>
                        <Link to={`location/${formattingFunctions.slugify(location.name)}`}>
                            <div className="h-[40vh] relative cursor-pointer overflow-hidden flex items-center justify-center">
                                <img className="absolute h-full  object-cover opacity-75 hover:opacity-50 w-full top-[50%] translate-y-[-50%] hover:scale-105 duration-500 " src={location.img} />
                                <h1 className="text-3xl  text-white py-8 font-mont px-10 uppercase z-10">{location.name}</h1>

                            </div>
                        </Link>
                    )}


                </div> */}
          <div
            className="px-10 max-w-[1700px] gap-x-12 mx-auto min-[900px]:grid pb-24 pt-16 -z-10"
            style={{ gridTemplateColumns: "40% 60%" }}
          >
            <div className="flex items-start flex-col gap-y-4 z-20 relative">
              <div className="max-[450px]:max-w-[100%] max-w-[70%] lg:max-w-[30vw] aspect-[1/1] overflow-clip z-30 mb-12 rounded-r-full">
                <img
                  src={getCurrentHeadshot()}
                  className="h-full w-full object-cover "
                />
              </div>
            </div>
            <div className="flex flex-col text-white text-xl  z-20 relative">
              <h1
                className={`text-4xl  text-white pb-8  libre tracking-wide z-10 mb-3 ease-in-out duration-2000 `}
              >
                About Me
              </h1>

              {getCurrentAbout() ? (
                getCurrentAbout().split("<|>").map((p, index) => (
                  <p key={index} className="min-[900px]:pr-[30%]">
                    {p}
                    <br />
                    <br />
                  </p>
                ))
              ) : (
                <p>Coming Soon</p>
              )}
              <div className="flex flex-wrap gap-x-4">
                {/* <btn onClick={() => contact.current.scrollIntoView()} className='btn-primary self-start mt-5 '>CONTACT US</btn> */}
                <Link className="btn-primary self-start mt-5 " to={"/about"}>
                  <button>
                    Why Choose {getCurrentAgentName().split(" ")[0]}
                  </button>
                </Link>
                <Link
                  className="btn-secondary self-start mt-5 "
                  to={"/contact"}
                >
                  <button>Contact</button>
                </Link>
              </div>
            </div>
          </div>
          <div className="flex text-white justify-between items-center pb-10 px-10 gap-x-5 max-w-[1700px] mx-auto">
            <h1
              className={`text-4xl libre uppercase ease-in-out duration-2000 `}
            >
              Testimonials
            </h1>
            {/* <Link className="underline font-semibold underline-offset-4 whitespace-nowrap " to={"/agents"}>View All</Link> */}
          </div>
          <div className="overflow-hidden w-[95vw] max-w-[1700px] mx-auto pb-24 pt-2 px-10">
            <div
              ref={agentsRef}
              style={{ marginLeft: offset }}
              className="flex  gap-x-10 "
            >
              {constants.TESTIMONIALS.concat(constants.TESTIMONIALS).map(
                (quote) => (
                  <div className=" bg-[#474747] duration-700 relative w-[600px]   text-white  group rounded-xl ">
                    {/* <div className="h-24 rounded-t-xl overflow-hidden w-full">
                                <img className="w-full h-full object-cover object-center blur-sm" src={agent.img ?? placeholder} />
    
                            </div> */}
                    <div className="w-[300px] py-8">
                      <div className="flex flex-col  gap-2 px-10  relative">
                        <div className="h-28 w-28 rounded-full overflow-hidden mb-4 self-center z-10 border-[3px] border-white">
                          <img
                            className="w-full h-full object-cover"
                            src={quote.img ?? placeholder}
                          />
                        </div>

                        <p className="text-lg libre font-semibold pb-2">
                          "{quote.quote}"
                        </p>
                        <p className=" italic">- {quote.name}</p>
                        {/* {quote.email &&
                                            <span><FontAwesomeIcon icon={faEnvelope} className="pr-2" />{quote.email}</span>
                                        } */}
                        {/* <div className="flex gap-x-4 mt-2 text-logo-main">
                                            {Object.entries(agent.socials ?? {})?.map(([k, v], index) =>
                                                <a href={v}>
                                                    <FontAwesomeIcon className="fa-lg" icon={constants.ICONS[k]} />

                                                </a>
                                            )}
                                        </div> */}
                      </div>
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        </div>

        <div className="w-full bg-[#f6ffa7] ">
          <div className="max-w-[1700px] mx-auto px-10 text-white pb-10">
            {/* <h1 className={`text-4xl py-12 libre ease-in-out duration-2000 slide-in ${windowSize > 700 ? 'slide-left' : ''}`}>Featured Neighbourhoods</h1> */}
            <div className="w-full py-16">
              <div
                style={{
                  backgroundImage: `url(${condosLarge})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundAttachment: "fixed",
                }}
                className="min-h-[80vh] md:min-h-[600px] relative  "
              >
                <div
                  style={{ backgroundColor: "rgb(0, 0, 0, .5)" }}
                  className="absolute h-full w-full z-10"
                ></div>
                <div className="h-full w-full text-white absolute z-20 text-center flex justify-center flex-col items-center gap-10">
                  <h1
                    style={{ fontWeight: "100" }}
                    className="text-3xl sm:text-4xl md:text-5xl px-3 libre"
                  >
                    Which Neighbourhood Fits Your Style?
                  </h1>
                  <p className=" text-xl px-10 max-w-[1400px]">
                    Toronto is often known as a city of neighbourhoods. Your
                    Realtor will work with you to make sure you find your best
                    fit for your dream home.
                  </p>
                </div>
              </div>
            </div>

            <div
              style={{
                gridTemplateColumns: `repeat( auto-fit, minmax(${
                  windowSize > 600 ? "300px" : "300px"
                }, 1fr) )`,
              }}
              className="grid gap-10 justify-center flex-wrap "
            >
              {constants.LOCATIONS.slice(0, 4).map((location, index) => (
                <Link
                  key={location.name}
                  to={`location/${formattingFunctions.slugify(location.name)}`}
                >
                  <div
                    ref={index === 0 ? locationRef : null}
                    className={`w-full aspect-[3/4] slide-up duration-1000 ease-in-out relative mt-10 cursor-pointer overflow-hidden flex items-center justify-center bg-black group`}
                  >
                    <img
                      className=" h-full  object-cover opacity-75 hover:opacity-100 w-full  group-hover:scale-105 duration-1000 "
                      src={location.img}
                    />
                    {/* <h1 className="text-3xl  text-white py-8 libre px-10  z-10">{location.name}</h1> */}
                    <div className=" px-4 py-2 duration-1000 h-[120px] bg-logo-second absolute bottom-0 w-full text-white group-hover:h-[130px]">
                      <div className="h-12 w-12 group-hover:scale-110 duration-1000 rounded-full bg-white text-logo-second flex items-center justify-center absolute top-[-20%] right-4">
                        <FontAwesomeIcon
                          className="fa-xl"
                          icon={faLocationDot}
                        />
                      </div>
                      <p className="text-2xl libre font-semibold pb-3">
                        {location.name}
                      </p>
                      <p>{location.description}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
          <div className="w-full bg-logo-second">
            <div className="px-10 mx-auto max-w-[1700px] text-white">
              <h1
                className={`text-4xl py-12 libre ease-in-out duration-2000 uppercase slide-in ${
                  windowSize > 700 ? "slide-left" : ""
                }`}
              >
                Useful Tools
              </h1>

              <div className="grid md:grid-cols-3 gap-10 child:overflow-hidden   pb-10">
                <Link className="group" to={"/calculator"}>
                  <div className="overflow-hidden h-96">
                    <img
                      className="h-full w-full object-cover group-hover:scale-105 duration-700"
                      src={calc}
                    />
                  </div>
                  <hr className="border-2 border-logo-main duration-1000 w-[20%] group-hover:w-[50%] mt-6 my-2"></hr>

                  <h3 className="text-2xl pt-2 pb-2 ">Mortgage Calculator</h3>
                  <span className="">
                    Find a price range that works with your budget by using our
                    Mortgage Calculator
                  </span>
                </Link>
                <Link className="group" to={"/real-estate-reports"}>
                  <div className="overflow-hidden h-96">
                    <img
                      className="h-full w-full object-cover group-hover:scale-105 duration-700"
                      src={report}
                    />
                  </div>
                  <hr className="border-2 border-logo-main duration-1000 w-[20%] group-hover:w-[50%] mt-6 my-2"></hr>

                  <h3 className="text-2xl pt-2 pb-2 ">Real Estate Reports</h3>
                  <span className="">
                    Gain valuable insights into the market with our free Real
                    Estate Reports
                  </span>
                </Link>
                <Link className="group" to={"/evaluation"}>
                  <div className="overflow-hidden h-96">
                    <img
                      className="h-full w-full object-cover group-hover:scale-105 duration-700"
                      src={house}
                    />
                  </div>
                  <hr className="border-2 border-logo-main duration-1000 w-[20%] group-hover:w-[50%] mt-6 my-2"></hr>

                  <h3 className="text-2xl pt-2 pb-2 ">Home Evaluation</h3>
                  <span className="">
                    How much is your home worth in today's market? Find out with
                    our free Home Evaluation
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div
          ref={contact}
          className="w-full bg-[#f2f2f2] flex flex-wrap gap-x-16 gap-y-10 justify-center py-16 px-7  text-black"
        >
          <div>
            <h4 className=" font-semibold text-lg min-w-[20vw]">
              Questions? We're Here To Help
            </h4>
            <form className="mt-6 flex flex-col">
              <div className="form-item">
                <label className="form-label">Name*</label>
                <input className="form-input" placeholder="Full Name"></input>
              </div>
              <div className="form-item">
                <label className="form-label">Email*</label>
                <input
                  className="form-input"
                  placeholder="Email Address"
                ></input>
              </div>
              <div className="form-item">
                <label className="form-label">Message*</label>
                <textarea
                  className="form-input resize-none h-32"
                  placeholder="Your Message"
                ></textarea>
              </div>
              <button className="btn-tertiary self-start  px-10">Send</button>
            </form>
          </div>
          <div>
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2885.1670922064877!2d-79.3108446!3d43.686289499999994!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89d4cc14ec855631%3A0xb3d0feb68a2d0ed3!2s85%20Cedarvale%20Ave%2C%20Toronto%2C%20ON%20M4C%204J7!5e0!3m2!1sen!2sca!4v1730912488107!5m2!1sen!2sca"
              height="365"
              width={`${windowSize > 900 ? "500" : "300"}`}
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>

            <div className="flex flex-col gap-y-2 mt-6">
              <h2 className="text-2xl">{getCurrentAgentName()}</h2>
              <p className="italic">
                {getCurrentAgentTitle()}, {getCurrentBrokerage()}
              </p>
              <p>
                <FontAwesomeIcon
                  icon={faPhone}
                  className="text-logo-second fa-lg"
                />{" "}
                {getCurrentAgentNumber()}{" "}
              </p>
              <p>
                <FontAwesomeIcon
                  icon={faEnvelope}
                  className="text-logo-second fa-lg"
                />{" "}
                {getCurrentAgentEmail()}{" "}
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
